import React, { useEffect, useMemo, useRef, forwardRef } from 'react';
import { useTheme } from 'styled-components';

import { CheckboxProps, CheckboxReturnValue } from './Checkbox.types';
import { Flex, Text } from 'src/packages/core';
import {
  Container,
  CheckboxWrapper,
  getCheckboxSize,
  getCheckMarkStyles,
  getIndeterminatekMarkStyles,
} from './Checkbox.styles';
import { DarkTheme, LightTheme } from 'src/packages/theme';

function useCombinedRefs(...refs: any) {
  const targetRef = useRef();
  React.useEffect(() => {
    refs.forEach((ref: any) => {
      if (!ref) return;

      if (typeof ref === 'function') {
        ref(targetRef.current);
      } else {
        ref.current = targetRef.current;
      }
    });
  }, [refs]);

  return targetRef;
}

const Checkbox: React.FC<CheckboxProps> = forwardRef((props, forwaredRef) => {
  const {
    errorMessage = '',
    label = undefined,
    note = '',
    disabled = false,
    checked = false,
    variant = 'primary',
    theme,
    withIndeterminateState = false,
    onChange = undefined,
    ...rest
  } = props;

  const contextTheme = useTheme();

  const defaultTheme = useMemo(() => {
    return theme
      ? theme === 'light'
        ? { ...LightTheme, rtl: contextTheme.rtl }
        : { ...DarkTheme, rtl: contextTheme.rtl }
      : contextTheme;
  }, [contextTheme, theme]);

  const inputRef = useRef(null);
  const checkedRef = useRef(checked);
  const combinedRef = useCombinedRefs([forwaredRef, inputRef, checkedRef]);

  const checkboxSize = useMemo(() => {
    return getCheckboxSize(variant);
  }, [variant]);

  const checkmarkStyles = useMemo(() => {
    return getCheckMarkStyles(variant);
  }, [variant]);

  const indeterminateMarkStyles = useMemo(() => {
    return getIndeterminatekMarkStyles(variant);
  }, [variant]);

  const updateInput = (ref: React.MutableRefObject<any>, checked: boolean | null) => {
    const input = ref.current;

    if (input) {
      input.checked = checked;

      if (withIndeterminateState) {
        input.indeterminate = checked == null;
      } else {
        input.indeterminate = false;
      }
    }
  };

  useEffect(() => {
    checkedRef.current = checked;
    updateInput(inputRef, checked);
  }, [checked]);

  const handleChange = () => {
    if (withIndeterminateState) {
      switch (checkedRef.current) {
        case true:
          checkedRef.current = false;
          break;
        case false:
          checkedRef.current = null;
          break;
        default:
          checkedRef.current = true;
          break;
      }
    } else {
      switch (checkedRef.current) {
        case true:
          checkedRef.current = false;
          break;
        default:
          checkedRef.current = true;
          break;
      }
    }

    updateInput(inputRef, checkedRef.current);

    if (typeof onChange === 'function') {
      onChange(checkedRef.current as CheckboxReturnValue);
    }
  };

  return (
    <Container {...rest}>
      <CheckboxWrapper
        themevariant={theme}
        defaulttheme={defaultTheme}
        checkboxSize={checkboxSize}
        checkmarkStyles={checkmarkStyles}
        indeterminateMarkStyles={indeterminateMarkStyles}
      >
        <input
          type={'checkbox'}
          ref={combinedRef as any}
          checked={checkedRef.current === null ? undefined : checkedRef.current}
          disabled={disabled}
          onChange={() => {}}
        />

        <span className={'checkbox'} onClick={handleChange} />

        {label || note ? (
          <Flex flexDirection={'column'} width={'fit-content'}>
            {label && typeof label !== 'string' && label}

            {label && typeof label === 'string' && <Text text={label} variant='p1' />}

            {note && <Text text={note} variant='p2' />}
          </Flex>
        ) : null}
      </CheckboxWrapper>

      {errorMessage && (
        <Text text={errorMessage} variant='p3' color={defaultTheme.colors.palette.red} />
      )}
    </Container>
  );
});

export default Checkbox;
